import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import Alert from '../Alert';
import { contactsApi } from '../../requests/api';

// Columns read as SHOUTING_SNAKE; the table shows them as words.
const FIELD_LABELS = {
    EMAIL_ID: 'Email',
    CELL: 'Cell',
    CELL_PHONE: 'Cell Phone',
    COMPANY_NAME: 'Company',
    COMPANY_ADDRESS: 'Company Address',
    COMPANY_CITY: 'Company City',
    COMPANY_STATE: 'Company State',
    COMPANY_ZIP: 'Company ZIP',
    ZIP: 'ZIP',
    FIRM_ID: 'Firm',
    LEAD_ID: 'Lead ID',
    CRM_ID: 'CRM ID',
    HSC_FOLLOW_UP: 'Peak Follow Up',
    VENDOR_TYPE: 'Vendor Type',
    PARENT_FIRM: 'Parent Firm',
};

const PRIORITY_FIELDS = [
    'NAME', 'FIRST_NAME', 'LAST_NAME', 'VENDOR_TYPE', 'VENDOR_SUBTYPE', 'EMAIL', 'EMAIL_ID',
    'CELL', 'CELL_PHONE', 'PHONE', 'WORK_PHONE', 'COMPANY_NAME', 'FIRM', 'FIRM_ID',
    'ADDRESS', 'CITY', 'STATE', 'ZIP', 'COMPANY_ADDRESS', 'COMPANY_CITY',
];

function fieldLabel(field) {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];

    return field
        .toLowerCase()
        .split('_')
        .map((word) => (word.length <= 3 && word !== 'the' ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' ');
}

function orderFields(fields) {
    const rank = (field) => {
        const priority = PRIORITY_FIELDS.indexOf(field.field);
        return priority === -1 ? PRIORITY_FIELDS.length : priority;
    };

    return fields.slice().sort((a, b) => rank(a) - rank(b));
}

function describe(record) {
    return [record.email, record.city].filter(Boolean).join(' · ') || '—';
}

export default function MergeContacts({ open, entity, label, plural, records, onClose, onMerged }) {
    // 'select' picks the records and the primary; 'fields' is the comparison.
    const [step, setStep] = React.useState('select');
    const [includedIds, setIncludedIds] = React.useState([]);
    const [primaryId, setPrimaryId] = React.useState(null);
    const [comparison, setComparison] = React.useState(null);
    // field => the id of the record whose value wins.
    const [choices, setChoices] = React.useState({});
    const [onlyDifferences, setOnlyDifferences] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) return;
        const ids = (records || []).map((record) => record.id);
        setStep('select');
        setIncludedIds(ids);
        setPrimaryId(ids[0] === undefined ? null : ids[0]);
        setComparison(null);
        setChoices({});
        setOnlyDifferences(true);
        setBusy(false);
        setError(null);
    }, [open, records]);

    if (!open) return null;

    const included = (records || []).filter((record) => includedIds.indexOf(record.id) !== -1);
    const lowerPlural = (plural || 'records').toLowerCase();

    const toggleIncluded = (id) => {
        const next = includedIds.indexOf(id) === -1
            ? includedIds.concat([id])
            : includedIds.filter((entry) => entry !== id);

        setIncludedIds(next);
        // The primary has to be one of the records being merged.
        if (next.indexOf(primaryId) === -1) setPrimaryId(next[0] === undefined ? null : next[0]);
        setError(null);
    };

    const loadComparison = () => {
        if (included.length < 2) {
            setError(`Pick at least two ${lowerPlural} to merge.`);
            return;
        }

        // Primary first: it is the second column, and the order of the rest is
        // the order of the columns after it.
        const ordered = [primaryId].concat(includedIds.filter((id) => id !== primaryId));

        setBusy(true);
        setError(null);

        contactsApi.compareDuplicates(entity, ordered)
            .then((data) => {
                // A merge keeps what every record knows: a field empty on the
                // primary starts on the first record that has a value.
                const defaults = {};
                data.fields.forEach((row) => {
                    const filled = row.blank_on_primary
                        ? ordered.find((id) => row.values[String(id)] !== null && row.values[String(id)] !== undefined)
                        : undefined;

                    defaults[row.field] = filled || primaryId;
                });

                setComparison(data);
                setChoices(defaults);
                setStep('fields');
                setBusy(false);
            })
            .catch((err) => {
                setBusy(false);
                setError(err.message || `Could not compare these ${lowerPlural}.`);
            });
    };

    const apply = () => {
        const mergeIds = includedIds.filter((id) => id !== primaryId);

        setBusy(true);
        setError(null);

        contactsApi.mergeDuplicates(entity, primaryId, mergeIds, choices)
            .then((data) => {
                setBusy(false);
                onMerged(data);
            })
            .catch((err) => {
                setBusy(false);
                setError(err.message || `Could not merge these ${lowerPlural}.`);
            });
    };

    const columns = comparison ? comparison.records : [];
    const rows = comparison
        ? orderFields(comparison.fields).filter((row) => !onlyDifferences || row.differs)
        : [];
    const differingCount = comparison ? comparison.fields.filter((row) => row.differs).length : 0;
    const otherCount = includedIds.length - 1;

    return (
        <Modal
            show={open}
            onClose={busy ? () => {} : onClose}
            className={`MergeProps ${step === 'fields' ? 'is-comparing' : ''}`}
        >
            <div className="MergeProps_body">
                <h2 className="MergeProps_title">
                    {`Merge ${plural} · Step ${step === 'select' ? '1' : '2'} of 2`}
                </h2>

                {step === 'select' ? (
                    <p className="MergeProps_intro">
                        {`Tick the ${lowerPlural} to merge together, and choose which one is the primary.
                        The primary is the record that is kept.`}
                    </p>
                ) : (
                    <p className="MergeProps_intro">
                        {`For each field, choose which ${(label || 'record').toLowerCase()}'s value the primary
                        should keep. The primary's own values are selected by default. Where the primary is
                        blank, the first ${(label || 'record').toLowerCase()} with a value is selected instead.`}
                    </p>
                )}

                {error ? <Alert type="error">{error}</Alert> : null}

                {step === 'select' ? (
                    <div className="MergeProps_scroll MergeProps_scroll--fit">
                        <table className="MergeProps_table MergeProps_select">
                            <thead>
                                <tr>
                                    <th>Merge</th>
                                    <th>Primary</th>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Cell</th>
                                    <th>Company</th>
                                    <th>City</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(records || []).map((record) => {
                                    const isIncluded = includedIds.indexOf(record.id) !== -1;

                                    return (
                                        <tr key={record.id} className={isIncluded ? '' : 'is-excluded'}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={isIncluded}
                                                    disabled={busy}
                                                    aria-label={`Merge ${record.id}`}
                                                    onChange={() => toggleIncluded(record.id)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="radio"
                                                    name="merge-primary"
                                                    checked={primaryId === record.id}
                                                    disabled={busy || !isIncluded}
                                                    aria-label={`Make ${record.id} the primary`}
                                                    onChange={() => setPrimaryId(record.id)}
                                                />
                                            </td>
                                            <td>{record.id}</td>
                                            <td>{record.name || ''}</td>
                                            <td>{record.email || ''}</td>
                                            <td>{record.cell || ''}</td>
                                            <td>{record.company || ''}</td>
                                            <td>{record.city || ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <React.Fragment>
                        <label className="MergeProps_toggle">
                            <input
                                type="checkbox"
                                checked={onlyDifferences}
                                disabled={busy}
                                onChange={(event) => setOnlyDifferences(event.target.checked)}
                            />
                            <span>
                                {`Only show the ${differingCount.toLocaleString()} field${differingCount === 1 ? '' : 's'} that differ`}
                            </span>
                        </label>

                        <div className="MergeProps_scroll">
                            <table className="MergeProps_table MergeProps_compare">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        {columns.map((record, index) => (
                                            <th key={record.id}>
                                                <span className="MergeProps_col_id">
                                                    {index === 0 ? `Primary · ${record.id}` : `#${record.id}`}
                                                </span>
                                                <span className="MergeProps_col_name">{record.name || describe(record)}</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={columns.length + 1} className="MergeProps_empty">
                                                {`These ${lowerPlural} hold the same value for every field.`}
                                            </td>
                                        </tr>
                                    ) : null}
                                    {rows.map((row) => (
                                        <tr key={row.field} className={row.differs ? 'is-differing' : ''}>
                                            <th scope="row">{fieldLabel(row.field)}</th>
                                            {columns.map((record) => {
                                                const value = row.values[String(record.id)];

                                                return (
                                                    <td key={record.id}>
                                                        <label className="MergeProps_value">
                                                            <input
                                                                type="radio"
                                                                name={`merge-field-${row.field}`}
                                                                checked={choices[row.field] === record.id}
                                                                disabled={busy}
                                                                aria-label={`${fieldLabel(row.field)} from ${record.id}`}
                                                                onChange={() =>
                                                                    setChoices({ ...choices, [row.field]: record.id })}
                                                            />
                                                            <span
                                                                className={value === null ? 'MergeProps_blank' : ''}
                                                                title={value || ''}
                                                            >
                                                                {value === null ? 'blank' : value}
                                                            </span>
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </React.Fragment>
                )}

                <div className="MergeProps_actions">
                    {step === 'fields' ? (
                        <span className="MergeProps_note">
                            {`The other ${otherCount} ${otherCount === 1 ? (label || 'record').toLowerCase() : lowerPlural} `
                                + `${otherCount === 1 ? 'is' : 'are'} deleted, and everything attached to `
                                + `${otherCount === 1 ? 'it' : 'them'} moves to the primary.`}
                        </span>
                    ) : null}
                    <button
                        type="button"
                        className={`button-new text ${busy ? 'inactive' : ''}`}
                        onClick={step === 'fields' ? () => setStep('select') : onClose}
                        disabled={busy}
                    >
                        {step === 'fields' ? 'Back' : 'Cancel'}
                    </button>
                    <button
                        type="button"
                        className={`button-new positive ${busy || included.length < 2 ? 'inactive' : ''}`}
                        onClick={step === 'select' ? loadComparison : apply}
                        disabled={busy || included.length < 2}
                    >
                        {busy
                            ? (step === 'select' ? 'Comparing…' : 'Merging…')
                            : (step === 'select'
                                ? `Compare ${included.length} ${lowerPlural}`
                                : `Merge into ${primaryId}`)}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

MergeContacts.propTypes = {
    open: PropTypes.bool.isRequired,
    entity: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    plural: PropTypes.string.isRequired,
    // The group's records, as the duplicate search returned them.
    records: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    // Called with { primary_id, merged_ids, fields_changed, rows_moved }.
    onMerged: PropTypes.func.isRequired,
};
